import React                                    from 'react'
import {Box}                                    from '@mui/material'
import {AutoAwesomeRounded}                     from '@mui/icons-material'
import {getCategoryIconComponent}               from '../util/category-icons'

/*
 * A category's icon, as a rounded tile filled with a 45° gradient of its two brand colours (102).
 *
 * One component for all three call sites -- the admin Categories list, the Add/Edit modal preview,
 * and the product medallion in the prepkit dashboard header -- so the tile an admin picks an icon
 * against is pixel-for-pixel the tile a student sees.
 *
 * Colours come off the category, but can be overridden: the modal previews against the colours
 * currently in the form, which are not yet saved.
 *
 * `placeholder` is the new-category state: no icon chosen yet, so a dashed neutral tile that reads
 * as an empty slot rather than a wrong guess. Everywhere else a missing icon falls back to
 * DEFAULT_CATEGORY_ICON, because a student-facing header must never render an empty box.
 *
 * `outlined` inverts the tile (105): a 2px gradient ring around the surface colour, with the icon
 * itself in the category's first colour. Opt-in rather than a change to the default, because the
 * admin screens preview against the filled tile and this component exists so that what an admin
 * picks an icon against is what a student sees.
 */
const FALLBACK_COLOR = '#4F46E5'

const CategoryIcon = ({
	category,
	icon,
	color,
	color2,
	size = 40,
	radius,
	placeholder = false,
	outlined = false,
	sx,
	...props
}) => {
	const name = icon !== undefined ? icon : category?.icon
	const c1 = color || category?.color || FALLBACK_COLOR
	const c2 = color2 || category?.color2 || c1
	const borderRadius = radius === undefined ? Math.round(size * 0.3) : radius

	if (placeholder && !name) {
		return (
			<Box
				sx={{
					flexShrink: 0,
					width: size,
					height: size,
					borderRadius: `${borderRadius}px`,
					border: '1px dashed',
					borderColor: 'divider',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: 'text.disabled',
					...sx,
				}}
				{...props}
			>
				<AutoAwesomeRounded sx={{fontSize: size * 0.45}}/>
			</Box>
		)
	}

	const Icon = getCategoryIconComponent(name)

	/*
	 * The ring is drawn as a gradient-filled box with 2px of padding and a surface-filled box
	 * inside it. `border-image` would be the direct way to do it, but it does not follow
	 * border-radius, so the corners of the ring would square off.
	 *
	 * The inner radius is 2px tighter than the outer, which is what keeps the ring an even width
	 * around the curve instead of pinching at the corners.
	 */
	if (outlined) {
		return (
			<Box
				sx={{
					flexShrink: 0,
					width: size,
					height: size,
					boxSizing: 'border-box',
					p: '2px',
					borderRadius: `${borderRadius}px`,
					background: `linear-gradient(45deg, ${c1}, ${c2})`,
					...sx,
				}}
				{...props}
			>
				<Box sx={{
					width: '100%',
					height: '100%',
					borderRadius: `${Math.max(borderRadius - 2, 0)}px`,
					// the theme surface, not a hardcoded white, so the ring does not punch a hole
					// in the page in dark mode
					background: 'background.paper',
					backgroundColor: 'background.paper',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}>
					<Icon sx={{fontSize: size * 0.5, color: c1}}/>
				</Box>
			</Box>
		)
	}

	return (
		<Box
			sx={{
				flexShrink: 0,
				width: size,
				height: size,
				borderRadius: `${borderRadius}px`,
				background: `linear-gradient(45deg, ${c1}, ${c2})`,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				...sx,
			}}
			{...props}
		>
			<Icon sx={{fontSize: size * 0.55, color: '#FFFFFF'}}/>
		</Box>
	)
}

export default CategoryIcon
